// src/components/shared/modals/PlanCopyModal.tsx
// Modal for copying content between plans.
import { useState, useEffect, type FormEvent } from 'react';
import { Copy, AlertTriangle, ArrowRight } from 'lucide-react';
import BaseModal from './BaseModal';
import ModalFooter from './ModalFooter';

export interface PlanCopyTarget {
  plan_name?: string;
  planNumber?: number | string;
  future_courses_count?: number;
  total_courses?: number;
  [key: string]: unknown;
}

export type PlanCopyType = 'all' | 'completed_only';

export interface PlanCopyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (copyType: PlanCopyType) => Promise<void> | void;
  fromPlan: PlanCopyTarget | null | undefined;
  toPlan: PlanCopyTarget | null | undefined;
  isLoading?: boolean;
}

const PlanCopyModal = ({
  isOpen,
  onClose,
  onConfirm,
  fromPlan,
  toPlan,
  isLoading = false,
}: PlanCopyModalProps) => {
  const [copyType, setCopyType] = useState<PlanCopyType>('all');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCopyType('all');
      setConfirmText('');
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const destCourseCount = toPlan?.future_courses_count || toPlan?.total_courses || 0;
    if (destCourseCount > 0 && confirmText !== 'REPLACE') {
      setError('You must type "REPLACE" exactly to confirm overwriting existing courses.');
      return;
    }

    try {
      await onConfirm(copyType);
      onClose();
    } catch (err) {
      setError((err as { message?: string })?.message || 'Failed to copy plan');
    }
  };

  if (!fromPlan || !toPlan) return null;

  const formatPlanName = (plan: PlanCopyTarget, planNumber: number | string | undefined): string => {
    const name = plan.plan_name || `Plan ${planNumber || ''}`;
    if (name.startsWith('Plan ')) {
      return name;
    }
    return `Plan ${planNumber || ''} · ${name}`;
  };

  const fromPlanName = formatPlanName(fromPlan, fromPlan.planNumber);
  const toPlanName = formatPlanName(toPlan, toPlan.planNumber);

  const destCourseCount = toPlan?.future_courses_count || toPlan?.total_courses || 0;
  const destHasCourses = destCourseCount > 0;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Copy Plan"
      icon={{ Component: Copy, bgColor: 'bg-status-progress/15', color: 'text-status-progress' }}
      maxWidth="max-w-lg"
      isLoading={isLoading}
    >
      <form onSubmit={handleSubmit}>
        <div className="p-6">
          <div className="flex items-center justify-center gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <p className="text-sm text-gray-500">From</p>
              <p className="font-medium text-gray-900">{fromPlanName}</p>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400" />
            <div className="text-center">
              <p className="text-sm text-gray-500">To</p>
              <p className="font-medium text-gray-900">{toPlanName}</p>
            </div>
          </div>

          <div className="space-y-3 mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What to copy:
            </label>

            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="copyType"
                value="all"
                checked={copyType === 'all'}
                onChange={(e) => setCopyType(e.target.value as PlanCopyType)}
                className="mt-1"
                disabled={isLoading}
              />
              <div>
                <p className="font-medium text-gray-900">Copy All</p>
                <p className="text-sm text-gray-500">
                  Copy all courses, semester structure, and majors/minors
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="copyType"
                value="completed_only"
                checked={copyType === 'completed_only'}
                onChange={(e) => setCopyType(e.target.value as PlanCopyType)}
                className="mt-1"
                disabled={isLoading}
              />
              <div>
                <p className="font-medium text-gray-900">Copy Completed Only</p>
                <p className="text-sm text-gray-500">
                  Copy only past semesters, AP Credits, and Other Credits. Future semesters will be
                  empty.
                </p>
              </div>
            </label>
          </div>

          {destHasCourses && (
            <>
              <div className="bg-status-warning/10 border border-status-warning/25 rounded-card p-4 mb-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">
                      {toPlanName} has {destCourseCount} courses
                    </p>
                    <p className="text-sm text-amber-700 mt-1">
                      This will replace all existing courses in the destination plan.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type <span className="font-mono bg-surface-sunken px-1 rounded">REPLACE</span> to
                  confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => {
                    setConfirmText(e.target.value);
                    setError(null);
                  }}
                  placeholder="REPLACE"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isLoading}
                />
              </div>
            </>
          )}

          {error && (
            <div className="mt-3 p-3 bg-status-critical/10 border border-status-critical/25 rounded-card">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        <ModalFooter
          onCancel={onClose}
          submitText="Copy Plan"
          loadingText="Copying..."
          variant="blue"
          isLoading={isLoading}
          disabled={destHasCourses && confirmText !== 'REPLACE'}
          submitType="submit"
          submitIcon={Copy}
        />
      </form>
    </BaseModal>
  );
};

export default PlanCopyModal;
